import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { PERSONAL_PROJECT_SCOPE } from "@/features/projects/types";
import { apiKeysRagApi, ApiKeyCreate } from "../service";
import {
  apiKeyQueryKeys,
  isApiKeyListKeyInScope,
  type ApiKeyListScope,
} from "../query-keys";
import { toast } from "sonner";
import i18n from "@/i18n";

function extractErrorMessage(error: any, fallbackKey: string): string {
  const data = error.response?.data;
  const message =
    data?.error?.message ||
    data?.detail ||
    data?.message;
  return message || i18n.t(fallbackKey);
}

interface UseApiKeysParams {
  page: number;
  pageSize: number;
  projectIds?: string[];
}

const useApiKeyListScope = () => {
  const { currentUser } = useAuth();
  const { currentOrganization, initialized } = useOrganization();

  return {
    scope: {
      principalId: currentUser?.id ?? "",
      organizationId:
        currentOrganization?.id ?? PERSONAL_PROJECT_SCOPE,
    } satisfies ApiKeyListScope,
    initialized,
  };
};

export const invalidateApiKeyQueryScope = (
  queryClient: QueryClient,
  scope: ApiKeyListScope,
) => {
  if (!scope.principalId) return Promise.resolve();

  return queryClient.invalidateQueries({
    queryKey: apiKeyQueryKeys.listScope(scope),
  });
};

export const useApiKeys = ({ page, pageSize, projectIds }: UseApiKeysParams) => {
  const { scope, initialized } = useApiKeyListScope();

  return useQuery({
    queryKey: apiKeyQueryKeys.list(scope, { page, pageSize, projectIds }),
    queryFn: () => apiKeysRagApi.listApiKeys(page, pageSize, projectIds),
    enabled: initialized === true && Boolean(scope.principalId),
    placeholderData: (previousData, previousQuery) =>
      previousQuery &&
      isApiKeyListKeyInScope(previousQuery.queryKey, scope)
        ? previousData
        : undefined,
  });
};

export const useCreateApiKey = () => {
  const queryClient = useQueryClient();
  const { scope } = useApiKeyListScope();

  return useMutation({
    mutationFn: (data: ApiKeyCreate) => apiKeysRagApi.createApiKey(data),
    onSuccess: () => {
      void invalidateApiKeyQueryScope(queryClient, scope);
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, "apiKeys.failedToCreate"));
    },
  });
};

export const useDeleteApiKey = () => {
  const queryClient = useQueryClient();
  const { scope } = useApiKeyListScope();

  return useMutation({
    mutationFn: (id: string) => apiKeysRagApi.deleteApiKey(id),
    onSuccess: () => {
      toast.success(i18n.t("apiKeys.deletedSuccessfully"));
      void invalidateApiKeyQueryScope(queryClient, scope);
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, "apiKeys.failedToDelete"));
    },
  });
};

export const useRotateApiKey = () => {
  const queryClient = useQueryClient();
  const { scope } = useApiKeyListScope();

  return useMutation({
    mutationFn: (id: string) => apiKeysRagApi.rotateApiKey(id),
    onSuccess: () => {
      void invalidateApiKeyQueryScope(queryClient, scope);
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, "apiKeys.failedToRotate"));
    },
  });
};
